import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import api from '../api/client';
import useIndicatorSeries from '../hooks/useIndicatorSeries';
import SignalChart from '../components/signal-chart/SignalChart';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  TrendingUp,
  TrendingDown,
  Activity,
  BarChart2,
  RefreshCw,
  PlayCircle,
  BarChart3
} from 'lucide-react';

const today = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () =>
  new Date(new Date().getFullYear(), new Date().getMonth(), 1)
    .toISOString()
    .slice(0, 10);

// ─── UTILS: Sentiment Visuals ──────────────────────────────────────────────────

const getScoreVisuals = (score) => {
  if (score == null) return { color: 'text-slate-500', bg: 'bg-slate-500/10', label: 'Neutral', Icon: Activity };
  if (score >= 0.8) return { color: 'text-win',  bg: 'bg-win/15',  border: 'border-win/30',  label: 'High Conviction', Icon: TrendingUp };
  if (score >= 0.5) return { color: 'text-win',  bg: 'bg-win/10',  border: 'border-win/20',  label: 'Bullish', Icon: TrendingUp };
  if (score <= 0.2) return { color: 'text-loss', bg: 'bg-loss/15', border: 'border-loss/30', label: 'Strong Sell', Icon: TrendingDown };
  if (score <= 0.5) return { color: 'text-loss', bg: 'bg-loss/10', border: 'border-loss/20', label: 'Bearish', Icon: TrendingDown };
  return { color: 'text-amber-500', bg: 'bg-amber-500/10', border: 'border-amber-500/20', label: 'Neutral', Icon: Activity };
};

// ─── Signals Page ─────────────────────────────────────────────────────────────

export default function Signals() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [screener, setScreener] = useState(null);
  const [filter, setFilter] = useState({ from: firstOfMonth(), to: today() });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillOut, setBackfillOut] = useState(null);

  const [selected, setSelected] = useState(null);
  const [showDeps, setShowDeps] = useState(false);
  const [showMeta, setShowMeta] = useState(false);

  // Indicator values come from the engine replaying the screener over this one
  // symbol, so the overlays are what the screener actually saw.
  const chart = useIndicatorSeries(id, selected?.symbol, selected?.date, {
    deps: showDeps,
  });

  useEffect(() => {
    api.get('/screeners').then((r) => {
      const s = r.data.find((x) => x.id === parseInt(id));
      setScreener(s || null);
    });
  }, [id]);

  const loadSignals = useCallback(async (f = filter) => {
    setLoading(true);
    setData(null);
    try {
      const res = await api.get(`/screeners/${id}/signals`, {
        params: { from: f.from, to: f.to },
      });
      setData(res.data);
    } catch (err) {
      setData({ error: err.response?.data?.error || 'Failed to load signals' });
    } finally {
      setLoading(false);
    }
  }, [id, filter]);

  useEffect(() => { loadSignals(); }, [loadSignals]); 

  const handleBackfill = async () => {
    setBackfilling(true);
    setBackfillOut(null);
    try {
      const res = await api.post(`/screeners/${id}/backfill`);
      setBackfillOut(res.data);
      if (res.data.exit_code === 0) loadSignals();
    } catch (err) {
      setBackfillOut({ error: err.response?.data?.error || 'Backfill failed' });
    } finally {
      setBackfilling(false);
    }
  };

  const grouped = () => {
    if (!data?.signals?.length) return [];
    const map = new Map();
    for (const s of data.signals) {
      if (!map.has(s.date)) map.set(s.date, []);
      map.get(s.date).push(s);
    }
    return [...map.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  };

  const runState = data?.run_state;

  return (
    <div className="flex flex-col h-screen bg-background">
      {/* Header Context */}
      <header className="flex-none flex flex-wrap items-center justify-between min-h-14 py-2 px-4 sm:px-6 bg-surface-base border-b border-border shadow-xs z-20 relative gap-y-2">
        <div className="flex items-center gap-2 sm:gap-3">
          <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground" onClick={() => navigate('/screeners')}>
            ← Screeners
          </Button>
          <div className="h-4 w-px bg-border mx-1"></div>
          <Activity size={18} className="text-primary" />
          <h1 className="font-semibold text-foreground tracking-tight">{screener?.name || 'Screener Signals'}</h1>
        </div>
        
        <div className="flex items-center gap-2 sm:gap-4 w-full sm:w-auto overflow-x-auto pb-1 sm:pb-0 hide-scrollbar">
          {runState && (
            <div className="flex flex-col items-end mr-4">
              <span className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider">Engine Status</span>
              <span className="text-xs text-foreground flex items-center gap-1.5">
                <div className={`w-2 h-2 rounded-full ${runState.last_run_date ? 'bg-win' : 'bg-amber-500'}`}></div>
                {runState.last_run_date ? runState.last_run_date : 'Pending Initialization'}
              </span>
            </div>
          )}
          <div className="flex items-center gap-2 bg-muted/30 p-1 rounded-md border border-border/50">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/builder/${id}`)}>Edit Code</Button>
            <Button variant="ghost" size="sm" onClick={() => navigate(`/backtest/${id}`)}>Backtest Logic</Button>
            <Button
              size="sm"
              className={backfilling ? 'animate-pulse bg-primary/80' : ''}
              onClick={handleBackfill}
              disabled={backfilling}
            >
              {backfilling ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <PlayCircle className="mr-2 h-4 w-4" />}
              {backfilling ? 'Computing...' : 'Run Backfill'}
            </Button>
          </div>
        </div>
      </header>

      {/* Backfill Logging Output */}
      {backfillOut && !backfilling && (
        <div className={`flex-none px-6 py-3 border-b border-border text-sm ${backfillOut.exit_code === 0 ? 'bg-win/5' : 'bg-loss/5'}`}>
          <pre className={`font-mono whitespace-pre-wrap ${backfillOut.exit_code === 0 ? 'text-win' : 'text-loss'}`}>
            {backfillOut.error || backfillOut.stderr || backfillOut.stdout || "Execution Complete"}
          </pre>
        </div>
      )}

      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Filter Controls */}
        <div className="flex-none flex flex-wrap items-center gap-2 sm:gap-4 px-4 sm:px-6 py-3 border-b border-border bg-surface-base/50 backdrop-blur-xs z-10">
          <div className="flex items-center gap-1 sm:gap-2 bg-background border border-border rounded-md px-2 py-1 shadow-xs w-full sm:w-auto">
            <label className="hidden sm:inline-block text-[11px] font-semibold uppercase text-muted-foreground ml-1">Range</label>
            <Input type="date" className="h-7 text-xs w-full sm:w-32 border-0 bg-transparent focus-visible:ring-0 shadow-none px-1" value={filter.from} onChange={(e) => setFilter(f => ({ ...f, from: e.target.value }))} />
            <span className="text-muted-foreground">—</span>
            <Input type="date" className="h-7 text-xs w-full sm:w-32 border-0 bg-transparent focus-visible:ring-0 shadow-none px-1" value={filter.to} onChange={(e) => setFilter(f => ({ ...f, to: e.target.value }))} />
          </div>
          <Button variant="default" size="sm" onClick={() => loadSignals()} disabled={loading} className="shadow-xs w-full sm:w-auto">
            Scan
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowMeta((v) => !v)}
            aria-pressed={showMeta}
            className={`hidden md:inline-flex shadow-xs text-xs ${showMeta ? 'text-foreground' : 'text-muted-foreground'}`}
            title="The chart plots these values for the selected row; show the column to compare them across rows"
          >
            {showMeta ? 'Hide' : 'Show'} raw metadata
          </Button>
        </div>

        {/* Core Layout: Split View */}
        <div className="flex-1 flex flex-col lg:flex-row overflow-hidden bg-muted/10">
          
          {/* Left: Signals Screener Table */}
          <div className="flex-1 overflow-auto border-b lg:border-b-0 lg:border-r border-border shadow-[inset_-4px_0_12px_rgba(0,0,0,0.02)] min-h-0">
            {loading && (
               <div className="flex flex-col items-center justify-center h-full text-muted-foreground space-y-4">
                 <RefreshCw className="h-8 w-8 animate-spin text-primary/50" />
                 <p className="text-sm font-medium">Scanning universe...</p>
               </div>
            )}

            {!loading && data?.error && (
              <div className="m-6 p-4 rounded-md bg-loss/10 border border-loss/20 text-loss text-sm">
                <Activity className="inline mr-2 mb-1" size={16}/> {data.error}
              </div>
            )}

            {!loading && data && !data.error && grouped().length === 0 && (
              <div className="flex flex-col items-center justify-center h-full px-4 text-center space-y-3">
                <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mb-2">
                  <BarChart2 className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold text-foreground">No Signals Found</h3>
                <p className="text-sm text-muted-foreground max-w-sm">No stocks matched your screener criteria for the selected period. Adjust your dates or run a full backfill.</p>
              </div>
            )}

            {!loading && grouped().length > 0 && (
            <div className="min-w-full inline-block align-middle">
              <table className="min-w-full text-left border-collapse relative">
                <thead className="sticky top-0 z-10 bg-surface-base/95 backdrop-blur-md shadow-xs border-b border-border">
                  <tr>
                    {/* Added responsive hidden classes to less critical columns */}
                    <th className="px-4 py-3 text-[10px] sm:text-xs font-bold uppercase tracking-wider text-muted-foreground w-20 sm:w-28">Date</th>
                    <th className="hidden sm:table-cell px-3 py-3 text-xs font-bold uppercase tracking-wider text-muted-foreground w-12">Rnk</th>
                    <th className="px-3 py-3 text-[10px] sm:text-xs font-bold uppercase tracking-wider text-muted-foreground w-28 sm:w-36">Symbol</th>
                    <th className="px-3 py-3 text-[10px] sm:text-xs font-bold uppercase tracking-wider text-muted-foreground w-32 sm:w-40">Conviction</th>
                    {showMeta && (
                      <th className="hidden md:table-cell px-3 py-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">Technical Meta</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/50">
                  {grouped().map(([date, rows]) =>
                    rows.map((r, i) => {
                      const isSelected = selected?.symbol === r.symbol && selected?.date === date;
                      const visuals = getScoreVisuals(r.score);
                      const { Icon } = visuals;
                      
                      return (
                        <tr
                          key={`${date}-${r.rank}`}
                          className={`cursor-pointer transition-all group ${isSelected ? 'bg-primary/5 border-l-2 border-l-primary' : 'hover:bg-muted/50 border-l-2 border-l-transparent'}`}
                          onClick={() =>
                            setSelected(isSelected ? null : { symbol: r.symbol, date })
                          }
                        >
                          {i === 0 && (
                            <td rowSpan={rows.length} className="px-4 py-3 text-[10px] sm:text-xs font-mono font-medium text-foreground align-top pt-4 border-r border-border/30 bg-surface-base/30 whitespace-nowrap">
                              {/* Show short date on mobile, full date on desktop */}
                              <span className="sm:hidden">{date.slice(5)}</span>
                              <span className="hidden sm:inline">{date}</span>
                            </td>
                          )}
                          <td className="hidden sm:table-cell px-3 py-4 text-xs font-medium text-muted-foreground">
                            {r.rank}
                          </td>
                          <td className="px-3 py-4">
                            <div className="inline-flex items-center gap-1 sm:gap-1.5 px-2 py-1 sm:px-2.5 rounded-md text-[10px] sm:text-xs font-bold">
                              <BarChart3 size={13} />
                              <span className="text-xs sm:text-sm font-bold text-foreground group-hover:text-primary transition-colors">
                                {r.symbol.replace('.NS', '')}
                              </span>
                            </div>
                          </td>
                          <td className="px-3 py-4">
                            <div className={`inline-flex items-center gap-1 sm:gap-1.5 px-2 py-1 sm:px-2.5 rounded-md text-[10px] sm:text-xs font-bold border ${visuals.bg} ${visuals.color} ${visuals.border}`}>
                              <Icon size={12} className="sm:w-[14px] sm:h-[14px]" />
                              {r.score != null ? r.score.toFixed(2) : '—'} 
                              {/* Trimmed to 2 decimals on mobile for space */}
                            </div>
                          </td>
                          {showMeta && (
                            <td className="hidden md:table-cell px-3 py-4 text-xs text-muted-foreground font-mono">
                              {r.metadata && Object.keys(r.metadata).length > 0
                                ? (
                                  <div className="flex flex-wrap gap-2">
                                    {Object.entries(r.metadata).map(([k, v]) => (
                                      <span key={k} className="bg-surface-base px-2 py-0.5 rounded border border-border/50 whitespace-nowrap">
                                        <span className="opacity-70 mr-1">{k}:</span>
                                        <span className="text-foreground">{typeof v === 'number' ? v.toFixed(2) : v}</span>
                                      </span>
                                    ))}
                                  </div>
                                )
                                : <span className="opacity-40 italic">No metadata</span>}
                            </td>
                          )}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
          </div>

          {/* Right: Presentation Chart Panel */}
          <div
            className={`${
              selected
                ? 'fixed inset-0 z-50 flex lg:static lg:z-auto'
                : 'hidden lg:flex'
            } lg:flex-none w-full lg:w-[55%] xl:w-[58%] lg:h-auto bg-background flex-col overflow-hidden relative shadow-[-8px_0_24px_rgba(0,0,0,0.03)] lg:border-t-0 border-border`}
          >
            {!selected ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center px-12 space-y-4">
                <div className="p-4 rounded-full bg-muted/50 border border-border border-dashed">
                  <BarChart2 className="w-10 h-10 text-muted-foreground opacity-50" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground">Awaiting Selection</h3>
                  <p className="text-sm text-muted-foreground mt-1 max-w-[250px]">Click a signal row to plot the indicators the screener used, and see which of its conditions held on each bar.</p>
                </div>
              </div>
            ) : (
              <>
                <div className="flex-none px-4 sm:px-6 py-3 sm:py-4 border-b border-border bg-surface-base flex justify-between items-center gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="lg:hidden -ml-2 px-2 text-muted-foreground"
                      onClick={() => setSelected(null)}
                      aria-label="Back to signals"
                    >
                      ← List
                    </Button>
                    <div className="min-w-0">
                      <h2 className="text-lg sm:text-xl font-bold text-foreground tracking-tight truncate">{selected.symbol.replace('.NS', '')}</h2>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs font-mono px-1.5 py-0.5 bg-primary/10 text-primary rounded">Signal Date</span>
                        <span className="text-sm font-medium text-muted-foreground">{selected.date}</span>
                      </div>
                    </div>
                  </div>
                  <Link
                    to={`/stock/${encodeURIComponent(selected.symbol.replace('.NS', ''))}`}
                    state={{ from: `/signals/${id}`, fromLabel: '← Signals' }}
                    className="inline-flex shrink-0 items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-border bg-background hover:bg-muted transition-colors text-foreground"
                  >
                    <BarChart3 size={13} />
                    Trendlyne
                  </Link>
                </div>
                
                <div className="flex-1 overflow-auto px-4 py-4">
                  {chart.loading && chart.ohlcv.length === 0 && (
                    <div className="h-full w-full flex flex-col items-center justify-center gap-3 text-muted-foreground">
                      <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground/50" />
                      <p className="text-xs">Replaying the screener over {selected.symbol.replace('.NS', '')}…</p>
                    </div>
                  )}

                  {chart.ohlcv.length > 0 && (
                    <div className="w-full border border-border/50 rounded-lg bg-surface-base shadow-lg p-3">
                      <SignalChart
                        ohlcv={chart.ohlcv}
                        series={chart.series}
                        evaluations={chart.evaluations}
                        blockers={chart.blockers}
                        warnings={chart.warnings}
                        error={chart.error}
                        signalDate={selected.date}
                        loading={chart.loading}
                        deps={showDeps}
                        onToggleDeps={setShowDeps}
                      />
                    </div>
                  )}

                  {!chart.loading && chart.ohlcv.length === 0 && (
                    <div className="h-full w-full flex items-center justify-center text-sm text-muted-foreground font-mono">
                      [ {chart.error || 'No chart data available'} ]
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}